import React, { useState, useEffect } from 'react';
import { Box, Drawer, IconButton, Menu, MenuItem, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from 'react-i18next';
import { useMasterData } from '../context/MasterDataContext';
import { MasterDataQuery } from '../../../services/query';
import Swal from 'sweetalert2';
import { MasterDataCommand } from '../../../services/command';
import { SCUIButton } from '../../../atomic/atoms';
import { SCUICustomDataGrid } from '../../../atomic/molecules';
import { MD, mdCls } from '../masterData.classes';

export const Platforms = () => {
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    const [platformId, setPlatformId] = useState<number>(0);
    const [platformName, setPlatformName] = useState<string>('');
    const [platforms, setPlatforms] = useState<any[]>([]);
    const [isEdit, setIsEdit] = useState<boolean>(false);

    const { t } = useTranslation();
    const { setIsLoading, isLoading } = useMasterData();

    const onDrawerClose = () => {
        setIsDrawerOpen(false);
    };

    const handleAddSupportedCountry = () => {
        setIsLoading(true);
        if (!isEdit) {
            MasterDataCommand.AddPlatform({
                PlatformId: platformId,
                PlatformName: platformName,
            })
                .then(() => {
                    getPlatforms();
                    Swal.fire({
                        position: 'center',
                        icon: 'success',
                        title: t('general.success'),
                        showConfirmButton: false,
                        timer: 1500,
                    });
                    onDrawerClose();
                })
                .catch(() => {
                    Swal.fire(t('general.anErrorOccured'), '', 'error');
                    setIsLoading(false);
                });
        } else {
            MasterDataCommand.UpdatePlatform({
                PlatformId: platformId,
                PlatformName: platformName,
            })
                .then(() => {
                    Swal.fire({
                        position: 'center',
                        icon: 'success',
                        title: t('general.success'),
                        showConfirmButton: false,
                        timer: 1500,
                    });
                    getPlatforms();
                    onDrawerClose();
                })
                .catch(() => {
                    Swal.fire(t('general.anErrorOccured'), '', 'error');
                    setIsLoading(false);
                });
        }
    };

    const handleDeletePlatform = (id: any) => {
        Swal.fire({
            text: t('general.sureText'),
            cancelButtonText: t('general.cancel'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('general.delete'),
        }).then((res: any) => {
            if (res.isConfirmed) {
                setIsLoading(true);
                MasterDataCommand.DeletePlatform({
                    PlatformId: id,
                })
                    .then(() => {
                        Swal.fire({
                            position: 'center',
                            icon: 'success',
                            title: t('general.success'),
                            showConfirmButton: false,
                            timer: 1500,
                        });
                        getPlatforms();
                        onDrawerClose();
                    })
                    .catch(() => {
                        Swal.fire(t('general.anErrorOccured'), '', 'error');
                        setIsLoading(false);
                    });
            }
        });
    };

    const columns = [
        {
            field: 'platformId',
            headerName: 'ID',
            maxWidth: 50,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'platformName',
            headerName: t('masterData.platformName'),
            flex: 1,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'menu',
            headerName: '',
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => {
                const handleClick = (popupState: any, tur: string) => {
                    popupState.close();
                    switch (tur) {
                        case 'gotoDetail': {
                            setPlatformId(params.row.platformId);
                            setPlatformName(params.row.platformName);
                            setIsDrawerOpen(true);
                            setIsEdit(true);
                            break;
                        }
                        case 'delete': {
                            handleDeletePlatform(params.row.platformId);
                            break;
                        }
                        default: {
                            break;
                        }
                    }
                };
                return (
                    <PopupState variant="popover" popupId="demo-popup-menu">
                        {(popupState: any) => (
                            <Box className={MD.datagridActionsCell}>
                                <IconButton
                                    {...bindTrigger(popupState)}
                                    className={mdCls('svg_icons', MD.inputIconRowActionsMenu)}
                                >
                                    <MoreVertIcon />
                                </IconButton>
                                <Menu
                                    {...bindMenu(popupState)}
                                    slotProps={{ paper: { className: MD.rowActionsMenuPaper } }}
                                >
                                    <MenuItem
                                        className={MD.inputMenuItemRowEdit}
                                        onClick={() => handleClick(popupState, 'gotoDetail')}
                                    >
                                        {t('general.edit')}
                                    </MenuItem>
                                    <MenuItem
                                        className={MD.inputMenuItemRowDelete}
                                        onClick={() => handleClick(popupState, 'delete')}
                                    >
                                        {t('general.delete')}
                                    </MenuItem>
                                </Menu>
                            </Box>
                        )}
                    </PopupState>
                );
            },
        },
    ];

    const getPlatforms = () => {
        setIsLoading(true);
        MasterDataQuery.GetPlatforms()
            .then((res: any) => {
                setPlatforms(res.data);
                setIsLoading(false);
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured'), '', 'error');
                setIsLoading(false);
            });
    };

    useEffect(() => {
        getPlatforms();
    }, []);

    return (
        <Box className={MD.platformsRoot} sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Box
                className={MD.platformsToolbar}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: '12px',
                    width: '100%',
                }}
            >
                <SCUIButton
                    className={MD.inputButtonCreatePlatform}
                    type="primary"
                    sizeType="fixed"
                    size="s"
                    text={t('masterData.createPlatform')}
                    showStartIcon
                    onClick={() => setIsDrawerOpen(true)}
                    startIcon={<AddIcon sx={{ color: '#fff' }} />}
                />
            </Box>
            <Box className={MD.platformsGridWrap}>
                <SCUICustomDataGrid hideFooter getRowId={(row: any) => row.platformId} columns={columns} rows={platforms} />
            </Box>
            <Drawer
                variant="temporary"
                anchor="right"
                open={isDrawerOpen}
                onClose={() => onDrawerClose()}
                slotProps={{
                    paper: {
                        className: MD.platformDrawerPaper,
                        sx: {
                            padding: '16px',
                            width: 300,
                        },
                    },
                }}
            >
                <Box className={MD.platformDrawerBody} sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: '16px' }}>
                    <p className={MD.platformDrawerTitle} style={{ fontWeight: '600', fontSize: '18px', marginBottom: '24px' }}>
                        {platformId ? t('masterData.updatePlatform') : t('masterData.createPlatform')}
                    </p>
                    <Box className={MD.platformDrawerIdField} sx={{ marginBottom: '8px' }}>
                        <p className={MD.platformDrawerIdLabel} style={{ fontWeight: '500', fontSize: '14px', marginBottom: '8px' }}>
                            ID
                        </p>
                        <TextField
                            className={MD.inputTextPlatformId}
                            value={platformId}
                            disabled={isEdit}
                            onChange={(event: any) => setPlatformId(event.target.value)}
                            fullWidth
                        />
                    </Box>
                    <Box className={MD.platformDrawerNameField} sx={{ marginBottom: '8px' }}>
                        <p className={MD.platformDrawerNameLabel} style={{ fontWeight: '500', fontSize: '14px', marginBottom: '8px' }}>
                            {t('masterData.platformName')}
                        </p>
                        <TextField
                            className={MD.inputTextPlatformName}
                            value={platformName}
                            onChange={(event: any) => setPlatformName(event.target.value)}
                            fullWidth
                        />
                    </Box>
                    <SCUIButton
                        className={MD.inputButtonPlatformSubmit}
                        style={{ marginTop: '12px', fontSize: '12px' }}
                        type="primary"
                        sizeType="fixed"
                        size="l"
                        fullwidth
                        text={platformId ? t('masterData.updatePlatform') : t('masterData.createPlatform')}
                        disabled={isLoading || !platformId || !platformName}
                        onClick={() => handleAddSupportedCountry()}
                    />
                </Box>
            </Drawer>
        </Box>
    );
};
